import React, { useContext, useEffect } from "react";

import { useFormContext } from "react-hook-form";

import { FormTranslationsContext } from "../../contexts/FormTranslationsContext";
import slugify from "../../utils/slug";

import type { JSX } from "react";

interface FormTranslationsProps {
  children: React.ReactNode;
  slugController?: string;
}

interface FormTranslationChildProps {
  name: string;
  sublabel?: string;
}

export default function FormTranslations({
  children,
  slugController,
}: FormTranslationsProps): JSX.Element | null {
  const { fieldArray, activeLanguageIndex } = useContext(
    FormTranslationsContext,
  );

  const fields = fieldArray?.fields ?? [];
  const methods = useFormContext();

  useEffect(() => {
    if (!slugController) return undefined;

    const formValuesWatch = methods.watch((_, { name: fieldName }) => {
      const fieldIndex = fieldName?.split(".")[1];

      if (
        fieldIndex &&
        fieldName === `translations.${fieldIndex}.${slugController}`
      ) {
        const pageName = methods.getValues(fieldName);

        const slug = slugify(`${pageName}`);

        methods.setValue(`translations.${fieldIndex}.slug`, slug);

        const { invalid } = methods.getFieldState(
          `translations.${fieldIndex}.slug`,
        );

        if (invalid) {
          methods.trigger(`translations.${fieldIndex}.slug`);
        }
      }
    });

    return () => {
      formValuesWatch.unsubscribe();
    };
  }, [methods, slugController, fieldArray]);

  const activeField = fields[activeLanguageIndex];

  if (!activeField) return null;

  return (
    <React.Fragment key={activeLanguageIndex}>
      {React.Children.map(children, (child) => {
        if (
          React.isValidElement<FormTranslationChildProps>(child) &&
          child.props.name
        ) {
          const { name, sublabel } = child.props;

          return React.cloneElement(child, {
            name: `translations.${activeLanguageIndex}.${name}`,
            sublabel,
          });
        }
        console.error(
          "Child components of FormTranslations must have a 'name' prop.",
        );
        return null;
      })}
    </React.Fragment>
  );
}
